import { createStore } from 'zustand/vanilla';
import { fetchRecipesByRestaurant, Recipe } from '../../../services/recipes/recipesService';

export interface RecipeViewerData {
  recipes: Recipe[];
  status: 'loading' | 'loaded' | 'error' | 'denied';
  isRefreshing: boolean;
  isStale: boolean;
  lastUpdated: number | null;
  refresh: () => Promise<void>;
  clear: () => void;
}

// Each mounted identity/restaurant owns a store. No shared dashboard cache or default selection.
export function createRecipeViewerStore(
  restaurantId: string,
  identityStillCurrent: () => boolean,
  fetchRecipes = fetchRecipesByRestaurant,
) {
  let generation = 0;
  let pending: Promise<void> | null = null;
  return createStore<RecipeViewerData>((set, get) => ({
    recipes: [], status: 'loading', isRefreshing: false, isStale: false, lastUpdated: null,
    clear: () => {
      generation++;
      pending = null;
      set({ recipes: [], status: 'loading', isRefreshing: false, isStale: false, lastUpdated: null });
    },
    refresh: () => {
      if (pending) return pending;
      if (!identityStillCurrent()) return Promise.resolve();
      const request = generation;
      set({ isRefreshing: true });
      pending = (async () => {
        try {
          const recipes = await fetchRecipes(restaurantId);
          if (request !== generation || !identityStillCurrent()) return;
          if (recipes.some(recipe => recipe.restaurantId !== restaurantId)) throw new Error('RECIPE_VIEWER_SCOPE_MISMATCH');
          set({ recipes, status: 'loaded', isStale: false, lastUpdated: Date.now() });
        } catch (error) {
          if (request !== generation || !identityStillCurrent()) return;
          console.error('Failed to load recipe viewer', error);
          const status = (error as { status?: number })?.status;
          const denied = status === 401 || status === 403 || (error instanceof Error && error.message === 'RECIPE_VIEWER_SCOPE_MISMATCH');
          set(denied
            ? { recipes: [], status: 'denied', isStale: true, lastUpdated: null }
            : { status: get().lastUpdated === null ? 'error' : 'loaded', isStale: true });
        } finally {
          if (request === generation) { pending = null; set({ isRefreshing: false }); }
        }
      })();
      return pending;
    },
  }));
}
